// src/Root.tsx
import React from 'react';
import { Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppProvider, useAppContext } from './contexts/AppContext';
import { ModalProvider } from './contexts/ModalContext';
import { TranslationProvider } from './contexts/TranslationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from 'react-hot-toast';
import './styles/ThemeSystem.css';
import { useSessionTracking } from './hooks/useSessionTracking';
import { usePageTracking } from './hooks/usePageTracking';
import { useSessionTime } from './hooks/useSessionTime';
import HealthyNavigationProvider from './components/HealthyNavigationProvider';
import RuntimeStabilityBanner from './components/RuntimeStabilityBanner';



// Bu bileşen, her iki context'in de yüklenmesini sırayla bekler.
const AppWrapper: React.FC = () => {
  const { loading: authLoading } = useAuth();
  const [loadingTimeout, setLoadingTimeout] = React.useState(false);
  
  // Tracking hooks
  useSessionTracking();
  usePageTracking();
  useSessionTime();
  
  // Auth sayfaları için loading kontrolünü atla
  const isAuthPage = window.location.pathname.startsWith('/auth/');
  
  // 10 saniye timeout
  React.useEffect(() => {
    if (authLoading && !isAuthPage) {
      const timer = setTimeout(() => {
        setLoadingTimeout(true);
        console.error('⚠️ Auth loading timeout - 10 saniye aşıldı');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [authLoading, isAuthPage]);
  
  // AuthContext yüklenirken bekleme ekranı göster (auth sayfaları hariç)
  if (authLoading && !isAuthPage) {
    if (loadingTimeout) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-red-50 to-orange-50">
          <div className="text-center max-w-md p-6">
            <div className="text-red-600 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Bağlantı Sorunu</h2>
            <p className="text-gray-600 mb-4">Sunucuya bağlanırken sorun yaşandı. Lütfen sayfayı yenileyin.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              🔄 Sayfayı Yenile
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-700 font-medium">Oturum kontrol ediliyor...</p>
          <p className="text-gray-500 text-sm mt-2">Lütfen bekleyin</p>
        </div>
      </div>
    );
  }

  // AuthContext hazırsa, AppProvider'ı ve onun içindeki asıl uygulamayı render et.
  // AppProvider'ın kendi içindeki useEffect, authLoading false olduğunda çalışmaya başlayacak.
  return (
    <AppProvider>
      <MainContent />
    </AppProvider>
  );
};

// Bu bileşen, AppContext'in yüklenmesini bekler.
const MainContent: React.FC = () => {
  const { isReady, renderGuard } = useAppContext();
  
  // Auth sayfaları için kontrol atla
  const isAuthPage = window.location.pathname.startsWith('/auth/');

  if (!isReady && !isAuthPage) {
    const guard = renderGuard();
    if (guard) return guard;
    return null;
  }
  
  // Her şey tamamen hazır, asıl sayfaları göster.
  return (
    <HealthyNavigationProvider
      options={{
        enableHealthCheck: true,
        enableAutoRecovery: true,
        performanceThreshold: 1000
      }}
    >
      <Outlet />
      <Toaster position="top-center" reverseOrder={false} />
      <RuntimeStabilityBanner />
    </HealthyNavigationProvider>
  );
};


const Root: React.FC = () => {
  return (
    <TranslationProvider>
      <ThemeProvider>
        <ModalProvider>
          <AuthProvider>
            <AppWrapper />
          </AuthProvider>
        </ModalProvider>
      </ThemeProvider>
    </TranslationProvider>
  );
};

export default Root;
